import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useOrderOnBehalf } from "./useOrderOnBehalf";

interface CartCountContextType {
  cartCount: number;
  supplementalCount: number;
}

interface CartContextType extends CartCountContextType {
  refreshCart: () => Promise<void>;
  addToCart: (productId: number, quantity?: number) => Promise<{ success: boolean; warning?: string; error?: string }>;
  clearCart: () => Promise<void>;
}

const CartCountContext = createContext<CartCountContextType>({
  cartCount: 0,
  supplementalCount: 0,
});

const CartContext = createContext<CartContextType>({
  cartCount: 0,
  supplementalCount: 0,
  refreshCart: async () => {},
  addToCart: async () => ({ success: false }),
  clearCart: async () => {},
});

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { customer: onBehalfCustomer } = useOrderOnBehalf();
  const queryClient = useQueryClient();

  const forCustomerId = onBehalfCustomer?.id ?? null;

  const buildUrl = useCallback((base: string) => {
    if (forCustomerId) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}forCustomerId=${forCustomerId}`;
    }
    return base;
  }, [forCustomerId]);

  // React Query: SWR-style cart count; 30 s stale, auto-refetches after mutations
  const cartQueryKey = ["cart", user?.id ?? null, forCustomerId];
  const { data: cartData } = useQuery({
    queryKey: cartQueryKey,
    queryFn: async () => {
      const supplementalUrl = forCustomerId
        ? `/api/cart/customer/${forCustomerId}`
        : user!.role !== "admin"
          ? "/api/cart/on-behalf-items"
          : null;
      const [mainRes, suppRes] = await Promise.all([
        fetch(buildUrl("/api/cart"), { credentials: "include" }),
        supplementalUrl ? fetch(supplementalUrl, { credentials: "include" }).catch(() => null) : Promise.resolve(null),
      ]);
      if (!mainRes.ok) return { cartCount: 0, supplementalCount: 0 };
      const mainData = await mainRes.json().catch(() => []);
      const mainItems: any[] = Array.isArray(mainData) ? mainData : [];
      const cartCount = mainItems.reduce((sum: number, item: any) => sum + item.quantity, 0);
      let supplementalCount = 0;
      if (suppRes && suppRes.ok) {
        const suppItems = await suppRes.json().catch(() => []);
        supplementalCount = Array.isArray(suppItems) ? suppItems.reduce((sum: number, item: any) => sum + item.quantity, 0) : 0;
      }
      return { cartCount, supplementalCount };
    },
    enabled: !!user,
    staleTime: 30_000,
    placeholderData: { cartCount: 0, supplementalCount: 0 },
  });

  const cartCount = cartData?.cartCount ?? 0;
  const supplementalCount = cartData?.supplementalCount ?? 0;

  const refreshCart = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["cart"] });
  }, [queryClient]);

  const addToCart = useCallback(async (productId: number, quantity: number = 1): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(buildUrl("/api/cart"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId, quantity, forCustomerId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { success: false, error: data.error || "Failed to add to cart" };
      }
      const data = await res.json().catch(() => ({}));
      await refreshCart();
      return { success: true, warning: data.warning };
    } catch {
      return { success: false, error: "Failed to add to cart" };
    }
  }, [refreshCart, buildUrl, forCustomerId]);

  const clearCart = useCallback(async () => {
    try {
      const res = await fetch(buildUrl("/api/cart"), { method: "DELETE", credentials: "include" });
      if (res.ok) {
        await queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
    } catch {}
  }, [buildUrl, queryClient]);

  const countValue = { cartCount, supplementalCount };
  const fullValue = { cartCount, supplementalCount, refreshCart, addToCart, clearCart };

  return (
    <CartCountContext.Provider value={countValue}>
      <CartContext.Provider value={fullValue}>
        {children}
      </CartContext.Provider>
    </CartCountContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}

export function useCartCount() {
  return useContext(CartCountContext);
}
